import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icona } from './Icona';

/*  «DA DOVE ESCE QUESTO NUMERO».

    Chiesto in riunione il 14/09/2026 fra le cose di grafica — «popup informativi sulle
    voci di costo» — e deciso con Andrea il 20/09: **si aprono con un clic**, non al
    passaggio del mouse. Con il mouse sarebbe un gesto in meno, ma il riquadro sparisce
    appena ci si allontana, e queste sono dieci righe di conti che qualcuno vuole leggere
    con calma, confrontare con un foglio e a volte copiare. Sul tablet, poi, il passaggio
    del mouse non esiste.

    Si chiude cliccando fuori o con Esc, come il menu di colonna: due riquadri che si
    aprono nella stessa schermata devono chiudersi allo stesso modo.

    `createPortal` e coordinate calcolate a mano per la stessa ragione del menu di
    colonna: il riquadro nasce dentro `.scheda`, che ha `overflow: auto`, e il riepilogo
    dei costi sta in fondo alla scheda — cioè proprio dove un pannello che si apre verso
    il basso verrebbe tagliato dal bordo. Uscendo dal contenitore il problema non esiste.
*/

export interface RigaSpiegazione {
  cosa: string;
  quanto?: string;
}

/*  Il bottone che apre la spiegazione: è il nome della voce, più una «i» che dice che
    c'è qualcosa da leggere. Il nome intero e non solo l'icona perché su una riga alta
    venti pixel un bersaglio di dodici è un bersaglio che si manca.  */
export function VoceSpiegata({
  nome,
  righe,
  children,
}: {
  nome: string;
  righe: RigaSpiegazione[];
  /** Quello che sta sotto il nome: la riga di contorno, sempre a schermo. */
  children?: React.ReactNode;
}) {
  const [aperta, setAperta] = useState(false);
  const bottone = useRef<HTMLButtonElement>(null);
  const id = useId();

  return (
    <>
      <button
        type="button"
        ref={bottone}
        className={`voce-spiegata${aperta ? ' aperta' : ''}`}
        aria-expanded={aperta}
        aria-controls={aperta ? id : undefined}
        aria-label={`Da dove esce ${nome}`}
        onClick={() => setAperta((x) => !x)}
      >
        <span className="nome">{nome}</span>
        <Icona nome="info" />
      </button>
      {children}
      {aperta && bottone.current !== null && (
        <Spiegazione
          id={id}
          titolo={nome}
          righe={righe}
          ancora={bottone.current}
          onChiudi={() => setAperta(false)}
        />
      )}
    </>
  );
}

function Spiegazione({
  id,
  titolo,
  righe,
  ancora,
  onChiudi,
}: {
  id: string;
  titolo: string;
  righe: RigaSpiegazione[];
  ancora: HTMLElement;
  onChiudi: () => void;
}) {
  const riquadro = useRef<HTMLDivElement>(null);
  const [posizione, setPosizione] = useState<{ top: number; left: number }>();

  useLayoutEffect(() => {
    function colloca() {
      const a = ancora.getBoundingClientRect();
      const larghezza = riquadro.current?.offsetWidth ?? 320;
      const altezza = riquadro.current?.offsetHeight ?? 220;
      const margine = 10;

      const left = Math.min(Math.max(margine, a.left), window.innerWidth - larghezza - margine);

      /*  Sotto se ci sta, sopra altrimenti. Il riepilogo dei costi sta in fondo alla
          scheda, quindi «sopra» è il caso normale e non l'eccezione.  */
      const sotto = window.innerHeight - a.bottom - margine;
      const top = altezza <= sotto ? a.bottom + 6 : Math.max(margine, a.top - altezza - 6);

      setPosizione({ top, left });
    }

    colloca();
    window.addEventListener('resize', colloca);
    /*  `true` sul terzo argomento: a scorrere è la scheda, non la finestra, e gli eventi
        di scorrimento di un contenitore non risalgono fino a window.  */
    window.addEventListener('scroll', colloca, true);
    return () => {
      window.removeEventListener('resize', colloca);
      window.removeEventListener('scroll', colloca, true);
    };
  }, [ancora]);

  useEffect(() => {
    function fuori(e: MouseEvent) {
      const bersaglio = e.target as Node;
      if (riquadro.current?.contains(bersaglio) || ancora.contains(bersaglio)) return;
      onChiudi();
    }
    function tasto(e: KeyboardEvent) {
      if (e.key === 'Escape') onChiudi();
    }
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', tasto);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', tasto);
    };
  }, [ancora, onChiudi]);

  return createPortal(
    <div
      id={id}
      className="spiegazione"
      role="dialog"
      aria-label={`Da dove esce ${titolo}`}
      ref={riquadro}
      style={
        posizione === undefined
          ? /*  Prima di sapere dove sta si disegna fuori vista: misurarlo serve, vederlo
                saltare in alto a sinistra e poi al suo posto no.  */
            { visibility: 'hidden' }
          : { top: posizione.top, left: posizione.left }
      }
    >
      <header>
        <strong>{titolo}</strong>
        <button type="button" className="chiudi" aria-label="Chiudi" onClick={onChiudi}>
          <Icona nome="chiudi" />
        </button>
      </header>
      <dl>
        {righe.map((r, i) => (
          /*  La chiave è l'indice, ed è uno dei casi in cui va bene: l'elenco si ricostruisce
              intero a ogni apertura e non si riordina né si filtra.  */
          <div key={i} className={r.quanto === undefined ? 'solo-testo' : ''}>
            <dt>{r.cosa}</dt>
            {r.quanto !== undefined && <dd>{r.quanto}</dd>}
          </div>
        ))}
      </dl>
    </div>,
    document.body,
  );
}
